/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { AvailabilityTag, utcStringToCalendarDate, formatDateTime } from 'sarsaparilla';

import { searchDate } from 'ui-search/src/utils/dateUtils';

import type { Availability, CarouselCardItem } from './CarouselCard.types';

interface AvailabilityBadgeProps {
    availability?: Availability;
}

export function AvailabilityBadge({ availability }: AvailabilityBadgeProps) {
    switch (availability) {
        case 'available':
            return <AvailabilityTag.Available />;
        case 'partially available':
            return <AvailabilityTag.PartiallyAvailable />;
        case 'not reservable':
            return <AvailabilityTag.NotReservable />;
        case 'lottery':
        case 'CAMPING_LOTTERY':
            return <AvailabilityTag.Lottery />;
        case 'unavailable':
            return <AvailabilityTag.Unavailable />;
        case 'new':
            return <AvailabilityTag.New />;
        case 'enter dates':
            return <AvailabilityTag.EnterDates />;
        case 'view availability':
            return <AvailabilityTag.ViewAvailability />;
        default:
            return null;
    }
}

export function getAvailabilityDatesToDisplay(item: CarouselCardItem): string {
    const startDateValue = utcStringToCalendarDate(item.available_dates?.[0]?.start_date);
    const endDateValue = utcStringToCalendarDate(item.available_dates?.[0]?.end_date);

    if (!startDateValue || !endDateValue) {
        return '';
    }

    const formatter = formatDateTime('en-US');
    const startDateMonth = formatter(startDateValue, { month: 'short' });
    const startDateDay = startDateValue.day;
    const endDateMonth = formatter(endDateValue, { month: 'short' });
    const endDateDay = endDateValue.day;

    if (startDateMonth === endDateMonth && startDateDay === endDateDay) {
        return `${startDateMonth} ${startDateDay}`;
    }

    if (startDateMonth === endDateMonth && startDateDay !== endDateDay) {
        return `${startDateMonth} ${startDateDay} - ${endDateDay}`;
    }

    return `${startDateMonth} ${startDateDay} - ${endDateMonth} ${endDateDay}`;
}

/**
 * Adds the availability dates in the weird search format to the legacy search session in local storage.
 * because ui-camping still uses the legacy search session to populate its date filter
 */
export function addAvailabilityDatesToLocalStorage(item: CarouselCardItem) {
    const availableDates = item?.available_dates?.[0];

    // Bail if the data is missing
    if (!availableDates || !availableDates.start_date || !availableDates.end_date) return;

    try {
        const startSearchDate = searchDate(availableDates.start_date).format(
            'MM/DD/YYYY'
        );
        const endSearchDate = searchDate(availableDates.end_date).format('MM/DD/YYYY');
        const maybeSearchSession = window.localStorage.getItem('r1s_search_session');

        if (maybeSearchSession) {
            const searchSession = JSON.parse(maybeSearchSession);
            searchSession.checkin_time = startSearchDate;
            searchSession.checkout_time = endSearchDate;
            window.localStorage.setItem(
                'r1s_search_session',
                JSON.stringify(searchSession)
            );
        }
    } catch (error) {
        // Eat the error
    }
}

// cSpell:ignore checkin_time
